export const VENDOR_FINANCE_METRIC_FILTER_MAP = {
  'ven-billed': { view: 'Bill', type: 'all' },
  'ven-paid': { view: 'Pay Bill', type: 'all' },
  'ven-pending': { view: 'Bill', type: 'open' },
  'ven-overdue': { view: 'Bill', type: 'overdue' }
};

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** Draft / cancelled bills are not payable obligations. */
export function isNonPayableBillStatus(status) {
  const st = String(status || '').trim().toLowerCase();
  return (
    st === 'draft'
    || st === 'submitted'
    || st === 'cancelled'
    || st === 'canceled'
  );
}

export function createEmptyVendorFinanceBucket() {
  return {
    totalBills: 0,
    overdueAmount: 0,
    amountPaidVendors: 0,
    outstandingVendor: 0,
    billCount: 0,
    overdueBillCount: 0,
    amountPaidBillCount: 0,
    outstandingBillCount: 0
  };
}

export function financeBucketToSummaryDTO(bucket) {
  return { ...bucket };
}

export function getVendorBillBalance(t) {
  if (!t) return 0;
  const raw = t.raw || t;
  const st = String(t.status || raw.status || '').toLowerCase();
  if (isNonPayableBillStatus(st)) return 0;
  if (raw.balanceAmount != null && raw.balanceAmount !== '') return num(raw.balanceAmount);
  if (raw.balance != null && raw.balance !== '') return num(raw.balance);
  const total = num(t.amount ?? t.originalAmount ?? t.total);
  const paid = num(raw.paidAmount ?? raw.amountPaid);
  if (paid > 0) return Math.max(0, total - paid);
  if (st === 'paid') return 0;
  return total;
}

function isBillOverdue(t, statusLower, startOfToday) {
  const bal = getVendorBillBalance(t);
  if (bal <= 0) return false;
  if (statusLower === 'overdue') return true;
  if (isNonPayableBillStatus(statusLower) || statusLower === 'paid') return false;
  if (!t.dueDate) return false;
  const due = new Date(t.dueDate);
  if (Number.isNaN(due.getTime())) return false;
  due.setHours(0, 0, 0, 0);
  return due < startOfToday;
}

export function applyVendorTransactionToFinanceBucket(t, bucket, startOfToday) {
  if (!t || !bucket) return;

  const type = String(t.type || '');
  if (type === 'Bill') {
    const st = String(t.status || '').toLowerCase();
    if (isNonPayableBillStatus(st)) return;
    bucket.billCount += 1;
    bucket.totalBills += num(t.amount);
    const bal = getVendorBillBalance(t);
    bucket.outstandingVendor += bal;
    if (bal > 0) {
      bucket.outstandingBillCount += 1;
    }
    if (isBillOverdue(t, st, startOfToday)) {
      bucket.overdueAmount += bal;
      bucket.overdueBillCount += 1;
    }
  }
  if (type === 'Pay Bill') {
    bucket.amountPaidBillCount += 1;
    bucket.amountPaidVendors += num(t.amount);
  }
}

export function buildVendorFinancialSummary(transactions) {
  const bucket = createEmptyVendorFinanceBucket();
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const list = Array.isArray(transactions) ? transactions : [];
  for (const t of list) {
    applyVendorTransactionToFinanceBucket(t, bucket, startOfToday);
  }
  return financeBucketToSummaryDTO(bucket);
}

export function buildVendorFinanceMetricRows(summary) {
  const s = summary || createEmptyVendorFinanceBucket();
  return [
    {
      key: 'ven-billed',
      label: 'Bills',
      count: s.billCount,
      amount: s.totalBills,
      overdue: false
    },
    {
      key: 'ven-paid',
      label: 'Pay bills',
      count: s.amountPaidBillCount,
      amount: s.amountPaidVendors,
      overdue: false
    },
    {
      key: 'ven-pending',
      label: 'Pending / Open',
      count: s.outstandingBillCount,
      amount: s.outstandingVendor,
      overdue: false
    },
    {
      key: 'ven-overdue',
      label: 'Overdue',
      count: s.overdueBillCount,
      amount: s.overdueAmount,
      overdue: true
    }
  ];
}
